import {Suspense, useEffect, useState} from 'react'

import {isFeatureEnabled} from '@github-ui/feature-flags'
import {TransactionalMessageBanner} from '@github-ui/in-product-targeting'
import {PencilIcon} from '@primer/octicons-react'
import {Button, IconButton, PageHeader} from '@primer/react'
import {useHeaderPageData} from '../page-data/loaders/use-header-page-data'
import {useAppPayload} from '@github-ui/react-core/use-app-payload'
import {PullRequestBanners} from './PullRequestBanners'
import {FilesChangedFeedback} from './FilesChangedFeedback'
import {PullRequestHeaderSummary} from './PullRequestHeaderSummary'
import {PullRequestEditTitleForm} from './PullRequestEditTitleForm'
import {PullRequestStateLabel} from './PullRequestStateLabel'
import type {HeaderPageData} from '../page-data/payloads/header'
import type {PullRequestAppPayload} from '../page-data/payloads/pull-request-app'
import {PullRequestHeaderNavigation} from './PullRequestHeaderNavigation'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import {useHeaderLiveUpdates} from '../hooks/use-header-live-updates'
import {PullRequestCodeButton} from './PullRequestCodeButton'
import {DiffStat} from './DiffStat'
import {clsx} from 'clsx'
import styles from './PullRequestHeader.module.css'
import responsiveStyles from './responsive.module.css'
import {LoadingSkeleton} from '@github-ui/skeleton/LoadingSkeleton'
import {isLoggedIn} from '@github-ui/client-env'
import {setTitle} from '@github-ui/document-metadata'
import {MergeStatusButtonWrapper} from '@github-ui/mergebox'
import {PullRequestState} from '../types'
import {StickyPullRequestHeader, type StickyPullRequestHeaderProps} from './StickyPullRequestHeader'

export type PullRequestHeaderProps = HeaderPageData & {
  isFilesRoute?: boolean
  stickyHeaderVariant?: StickyPullRequestHeaderProps['pageVariant']
}

/**
 * LivePullRequestHeader is a thin wrapper around PullRequestHeader that will automatically update the header when the
 * alive channel is triggered with `{title_updated: true}`. This lives here instead of the base page so that
 * it doesn't re-render the entire page when the title is updated.
 */
export function LivePullRequestHeader({
  aliveChannel,
  bannersData,
  isFilesRoute,
  stickyHeaderVariant,
  pullRequest,
  pageTitle,
  repository,
  urls,
  user,
  mergeStatusButtonData,
  stack,
}: PullRequestHeaderProps & {aliveChannel: string; isFilesRoute?: boolean}) {
  const {data: headerData} = useHeaderPageData({
    aliveChannel,
    bannersData,
    pageTitle,
    pullRequest,
    repository,
    stack,
    urls,
    user,
    mergeStatusButtonData,
  })
  useHeaderLiveUpdates(aliveChannel)

  return <PullRequestHeader {...headerData} isFilesRoute={isFilesRoute} stickyHeaderVariant={stickyHeaderVariant} />
}

export function PullRequestHeader({
  bannersData,
  isFilesRoute,
  stickyHeaderVariant,
  pullRequest,
  repository,
  pageTitle,
  urls,
  user,
  mergeStatusButtonData,
  stack,
}: PullRequestHeaderProps) {
  const [isEditing, setIsEditing] = useState(false)
  const {transactionalMessageBanner} = useAppPayload<PullRequestAppPayload>()
  const editButtonRedesign = isFeatureEnabled('pull_request_edit_button_redesign')

  const shouldShowLoggedInOptions = isLoggedIn()
  const isPROpen = pullRequest.state !== PullRequestState.Closed && pullRequest.state !== PullRequestState.Merged
  const {channels, headSha, helpUrl, viewerLogin} = mergeStatusButtonData || {}

  useEffect(() => {
    if (pageTitle) {
      setTitle(pageTitle)
    }
  }, [pageTitle])

  const titleActions = (renderLocation: 'mobile' | 'desktop') => (
    <>
      <div className="js-pull-header-details" data-pull-is-open={isPROpen ? 'true' : 'false'} hidden />
      {isFilesRoute && <FilesChangedFeedback repository={repository} urls={urls} renderLocation={renderLocation} />}
      {shouldShowLoggedInOptions && (
        <div className="d-flex gap-2">
          {user.canEditTitle && !editButtonRedesign && (
            <Button onClick={() => setIsEditing(true)} size="small">
              Edit
            </Button>
          )}
          <PullRequestCodeButton
            codespacesEnabled={repository.codespacesEnabled}
            headBranch={pullRequest.headBranch}
            isEnterprise={repository.isEnterprise}
            pullRequestNumber={pullRequest.number}
            repository={repository}
          />
        </div>
      )}
    </>
  )

  return (
    <>
      {isFeatureEnabled('ipm_global_transactional_message_prs') && transactionalMessageBanner ? (
        <div className="pr-mb-3">
          <TransactionalMessageBanner banner={transactionalMessageBanner} />
        </div>
      ) : null}
      {isEditing && (
        <PullRequestEditTitleForm
          initialTitle={pullRequest.title}
          pullRequestNumber={pullRequest.number}
          onCloseForm={() => setIsEditing(false)}
        />
      )}
      {/* This is a hack to make the title actions appear above the title on mobile */}
      {!isEditing && (
        <div
          className={clsx(
            responsiveStyles['hide-on-desktop'],
            'pb-2 pr-mb-3 flex-md-order-1 flex-shrink-0 d-flex flex-items-center gap-1 position-relative',
          )}
        >
          {titleActions('mobile')}
        </div>
      )}
      <PageHeader className="flex-items-center">
        {isEditing && (
          // this is done so that we still have an h1 element present when editing the title
          <h1 className="sr-only">{`${pullRequest.title} - #${pullRequest.number}`}</h1>
        )}
        {!isEditing && (
          <>
            <PageHeader.TitleArea>
              <PageHeader.Title as="h1" className="lh-condensed">
                <VerifiedHTMLText className="f1 text-normal markdown-title" html={pullRequest.titleHtml} />
                <span className="pl-2 fgColor-muted f1-light d-inline">#{pullRequest.number}</span>
                {user.canEditTitle && editButtonRedesign && (
                  <IconButton
                    icon={() => <PencilIcon size={18} />}
                    onClick={() => setIsEditing(true)}
                    size="medium"
                    aria-label="Edit title"
                    variant="invisible"
                  />
                )}
              </PageHeader.Title>
            </PageHeader.TitleArea>
            <PageHeader.Actions
              className={clsx(responsiveStyles['hide-on-mobile'], 'flex-items-center gap-1 position-relative')}
            >
              {titleActions('desktop')}
              {isFeatureEnabled('pull_request_merge_status_button') &&
                channels &&
                headSha &&
                helpUrl &&
                viewerLogin && (
                  <MergeStatusButtonWrapper
                    channels={channels}
                    headSha={headSha}
                    helpUrl={helpUrl}
                    viewerLogin={viewerLogin}
                  />
                )}
            </PageHeader.Actions>
          </>
        )}
        <PageHeader.Description className="d-flex flex-column flex-items-start">
          <div className="d-flex flex-column flex-sm-row gap-2 width-full flex-items-start flex-justify-between">
            <PullRequestStateLabel state={pullRequest.state} />
            <div className="flex-1">
              <PullRequestHeaderSummary
                author={pullRequest.author}
                mergedTime={pullRequest.mergedTime}
                baseBranch={pullRequest.baseBranch}
                baseRepositoryDefaultBranch={repository.defaultBranch}
                baseRepositoryName={repository.name}
                baseRepositoryOwnerLogin={repository.ownerLogin}
                canChangeBase={user.canChangeBase}
                commitsCount={pullRequest.commitsCount}
                headBranch={pullRequest.headBranch}
                headRepositoryOwnerLogin={pullRequest.headRepositoryOwnerLogin}
                headRepositoryName={pullRequest.headRepositoryName}
                isInAdvisoryRepo={pullRequest.isInAdvisoryRepo}
                isEditing={isEditing}
                mergedBy={pullRequest.mergedBy}
                mergedByName={pullRequest.mergedByName}
                repositoryId={repository.id}
                pullRequestId={pullRequest.id}
                pullRequestNumber={pullRequest.number}
                setIsEditing={setIsEditing}
                state={pullRequest.state}
                stack={stack}
              />
            </div>
          </div>
          <PullRequestBanners bannersData={bannersData} pullRequest={pullRequest} repository={repository} />
        </PageHeader.Description>
        <PageHeader.Navigation className="pr-pt-3 pr-px-3 pr-ml-n3 pr-mr-n3">
          <div className={clsx(styles.diffStatesWrapper, 'float-right d-none d-md-block')}>
            <Suspense fallback={<LoadingSkeleton width="80px" />}>
              <DiffStat />
            </Suspense>
          </div>
          <div className="flex-auto">
            <PullRequestHeaderNavigation pullRequest={pullRequest} repository={repository} urls={urls} />
          </div>
        </PageHeader.Navigation>
      </PageHeader>
      {!isFilesRoute && (
        <StickyPullRequestHeader
          repository={repository}
          pullRequest={pullRequest}
          pageVariant={stickyHeaderVariant ?? 'condensed'}
        />
      )}
    </>
  )
}
